const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.tcf-studio.dev';

const CHUNK_SIZE = 100 * 1024 * 1024; // 100 MB

class WeightService {
  getHeaders() {
    const token = localStorage.getItem('authToken');
    return { Authorization: `Bearer ${token}` };
  }

  getJsonHeaders() {
    return { ...this.getHeaders(), 'Content-Type': 'application/json' };
  }

  // ── hashing ────────────────────────────────────────────────────────

  /**
   * Compute SHA-256 hash of a file using Web Crypto API (streaming).
   * Returns hex string.
   * @param {File} file
   * @param {function} onProgress - callback(bytesRead, totalBytes)
   */
  async computeFileHash(file, onProgress) {
    const chunkSize = 2 * 1024 * 1024; // 2 MB read chunks for hashing
    const total = file.size;
    let offset = 0;

    // Use SubtleCrypto for streaming hash
    // We need to read the file in chunks and feed into a digest
    // Unfortunately SubtleCrypto doesn't support streaming, so we accumulate
    // For files > 1GB this may use significant memory
    // A better approach would be a WASM SHA-256, but this works for v1

    const buffer = await file.arrayBuffer();
    if (onProgress) onProgress(total, total);

    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Compute per-chunk SHA-256 hashes (one hash per 100MB chunk).
   * @param {File} file
   * @param {function} onProgress - callback(chunkIndex, totalChunks)
   * @returns {string[]} array of hex hash strings
   */
  async computeChunkHashes(file, onProgress) {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const hashes = [];

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const buffer = await chunk.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      hashes.push(hashArray.map(b => b.toString(16).padStart(2, '0')).join(''));

      if (onProgress) onProgress(i + 1, totalChunks);
    }

    return hashes;
  }

  // ── upload flow ────────────────────────────────────────────────────

  /**
   * Full upload flow with dedup detection:
   * 1. Compute hash
   * 2. Init (check dedup)
   * 3. Upload if needed (single or chunked)
   * 4. Complete if chunked
   *
   * @param {File} file
   * @param {object} opts - { curatedImageId, onHashProgress, onUploadProgress, onStatus }
   * @returns {{ success, url, deduplicated }}
   */
  async uploadWeight(file, { curatedImageId, onHashProgress, onUploadProgress, onStatus } = {}) {
    try {
      // Step 1: Compute hashes
      if (onStatus) onStatus('hashing');

      const sha256 = await this.computeFileHash(file, (read, total) => {
        if (onHashProgress) onHashProgress(read / total);
      });

      let chunkHashes = null;
      if (file.size > CHUNK_SIZE) {
        chunkHashes = await this.computeChunkHashes(file, (done, total) => {
          if (onHashProgress) onHashProgress(done / total);
        });
      }

      // Step 2: Init upload (dedup check)
      if (onStatus) onStatus('checking');

      const initRes = await fetch(`${API_BASE_URL}/storage/weights/init`, {
        method: 'POST',
        headers: this.getJsonHeaders(),
        body: JSON.stringify({
          sha256,
          filename: file.name,
          size: file.size,
          contentType: file.type || 'application/octet-stream',
          curatedImageId: curatedImageId || null,
          chunkHashes,
        }),
      });
      const initData = await initRes.json();

      if (!initData.success) {
        return { success: false, error: initData.error };
      }

      // Deduplicated — no upload needed
      if (initData.status === 'deduplicated') {
        if (onStatus) onStatus('deduplicated');
        return { success: true, url: initData.url, deduplicated: true, message: initData.message };
      }

      // Step 3: Upload
      if (onStatus) onStatus('uploading');

      if (initData.status === 'upload') {
        // Single upload (≤ 100MB)
        return await this._uploadSingle(file, initData.uploadToken, onUploadProgress);
      }

      if (initData.status === 'upload_chunked') {
        // Chunked upload (> 100MB)
        return await this._uploadChunked(file, initData, onUploadProgress, onStatus);
      }

      return { success: false, error: 'Unknown upload status' };
    } catch (error) {
      console.error('Upload weight error:', error);
      return { success: false, error: error.message || 'Upload failed' };
    }
  }

  async _uploadSingle(file, uploadToken, onUploadProgress) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadToken', uploadToken);

    const response = await fetch(`${API_BASE_URL}/storage/weights/upload`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: formData,
    });

    const data = await response.json();
    if (onUploadProgress) onUploadProgress(1);
    return { success: data.success, url: data.url, deduplicated: false };
  }

  async _uploadChunked(file, initData, onUploadProgress, onStatus) {
    const { uploadId, totalParts, chunkSize, r2Key } = initData;

    // Upload each chunk
    for (let part = 1; part <= totalParts; part++) {
      const start = (part - 1) * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const response = await fetch(
        `${API_BASE_URL}/storage/weights/chunk?uploadId=${encodeURIComponent(uploadId)}&part=${part}`,
        {
          method: 'POST',
          headers: {
            ...this.getHeaders(),
            'Content-Type': 'application/octet-stream',
          },
          body: chunk,
        },
      );

      const data = await response.json();
      if (!data.success) {
        return { success: false, error: `Chunk ${part} failed: ${data.error}` };
      }

      if (onUploadProgress) onUploadProgress(part / totalParts);
    }

    // Complete multipart upload
    if (onStatus) onStatus('completing');

    const completeRes = await fetch(`${API_BASE_URL}/storage/weights/complete`, {
      method: 'POST',
      headers: this.getJsonHeaders(),
      body: JSON.stringify({ uploadId }),
    });

    const completeData = await completeRes.json();
    return {
      success: completeData.success,
      url: completeData.url,
      deduplicated: false,
    };
  }

  // ── CRUD ───────────────────────────────────────────────────────────

  async listWeights({ curatedImageId, limit = 100, offset = 0 } = {}) {
    try {
      const params = new URLSearchParams();
      if (curatedImageId) params.set('curated_image_id', String(curatedImageId));
      if (limit) params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));

      const response = await fetch(`${API_BASE_URL}/storage/weights?${params}`, {
        headers: this.getHeaders(),
      });
      return await response.json();
    } catch (error) {
      console.error('List weights error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async getWeight(weightId) {
    try {
      const response = await fetch(`${API_BASE_URL}/storage/weights/${weightId}`, {
        headers: this.getHeaders(),
      });
      return await response.json();
    } catch (error) {
      console.error('Get weight error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async deleteWeight(weightId) {
    try {
      const response = await fetch(`${API_BASE_URL}/storage/weights/${weightId}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });
      return await response.json();
    } catch (error) {
      console.error('Delete weight error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async getUsage() {
    try {
      const response = await fetch(`${API_BASE_URL}/storage/usage`, {
        headers: this.getHeaders(),
      });
      return await response.json();
    } catch (error) {
      console.error('Usage error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async adminListBlobs({ limit = 200, offset = 0 } = {}) {
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      const response = await fetch(`${API_BASE_URL}/storage/admin/blobs?${params}`, {
        headers: this.getHeaders(),
      });
      return await response.json();
    } catch (error) {
      console.error('Admin blobs error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async listWeightsByImage(imageId) {
    try {
      const response = await fetch(`${API_BASE_URL}/storage/weights/image/${imageId}`, {
        headers: this.getHeaders(),
      });
      return await response.json();
    } catch (error) {
      console.error('Weights by image error:', error);
      return { success: false, error: 'Network error' };
    }
  }
}

const weightService = new WeightService();
export default weightService;
